import { useState } from "react";
import { BACKEND_URL } from "@/lib/api";
import axios from "axios";
import {
  BarChart3,
  Shield,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  Shuffle,
} from "lucide-react";

export default function ReportView() {
  const [seed, setSeed] = useState(42);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const randomSeed = () => setSeed(Math.floor(Math.random() * 99999));

  const generateReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("cf_token");
      const res = await axios.get(`${BACKEND_URL}/api/generate/distribution-report?seed=${seed}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReport(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tab-content" data-testid="report-tab">
      <h2 className="tab-title">Response Distribution Report</h2>
      <p className="tab-desc">
        Analyse how varied the generated responses are — verify the campaign won't be flagged for content homogeneity
      </p>

      <div className="gen-controls">
        <div className="gen-seed-row">
          <label>Analyse Seed</label>
          <div className="gen-seed-input">
            <input
              data-testid="report-seed-input"
              type="number"
              value={seed}
              onChange={(e) => setSeed(parseInt(e.target.value) || 0)}
            />
            <button className="btn-icon" onClick={randomSeed} title="Random seed" data-testid="report-random-seed">
              <Shuffle size={16} />
            </button>
          </div>
        </div>
        <button
          data-testid="run-report-btn"
          className="btn-primary gen-btn"
          onClick={generateReport}
          disabled={loading}
        >
          <BarChart3 size={18} />
          <span>{loading ? "Analysing..." : "Run Analysis"}</span>
        </button>
      </div>

      {error && (
        <div className="gen-error" data-testid="report-error">
          <AlertTriangle size={16} /> <span>{error}</span>
        </div>
      )}

      {report && (
        <div className="report-content" data-testid="report-content">
          {/* Health Score */}
          <HealthBanner report={report} />

          {/* Boolean Distribution */}
          <div className="report-section" data-testid="bool-distribution">
            <h3 className="report-section-title">Boolean Field Distribution</h3>
            <p className="report-section-desc">Yes/No answers compared to realistic population targets</p>
            <div className="bool-grid">
              {Object.entries(report.bool_distribution).map(([key, d]) => (
                <BoolCard key={key} data={d} total={report.total_sites} />
              ))}
            </div>
          </div>

          {/* Text Variation */}
          <div className="report-section" data-testid="text-distribution">
            <h3 className="report-section-title">Text Response Variation</h3>
            <p className="report-section-desc">How responses are distributed across template variants</p>
            <div className="text-dist-list">
              {Object.entries(report.text_distribution).map(([key, d]) => (
                <TextDistCard key={key} data={d} total={report.total_sites} />
              ))}
            </div>
          </div>

          {/* Content Similarity */}
          <div className="report-section" data-testid="similarity-section">
            <h3 className="report-section-title">Content Similarity Check</h3>
            <div className="similarity-grid">
              <div className="sim-card">
                <span className="sim-value">{report.content_similarity.total_unique_openings}</span>
                <span className="sim-label">Unique response openings</span>
              </div>
              <div className="sim-card">
                <span className="sim-value">{report.content_similarity.max_identical_openings}</span>
                <span className="sim-label">Max sites sharing same opening</span>
              </div>
              <div className="sim-card">
                <span className="sim-value">{report.total_sites}</span>
                <span className="sim-label">Total sites analysed</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HealthBanner({ report }) {
  const isLow = report.risk_level === "LOW";
  const isMed = report.risk_level === "MEDIUM";
  const Icon = isLow ? ShieldCheck : isMed ? Shield : ShieldAlert;
  const color = isLow ? "var(--cf-green)" : isMed ? "var(--cf-orange)" : "var(--cf-red)";
  const bgClass = isLow ? "health-low" : isMed ? "health-med" : "health-high";

  return (
    <div className={`health-banner ${bgClass}`} data-testid="health-banner">
      <div className="health-left">
        <Icon size={32} style={{ color }} />
        <div>
          <div className="health-score-row">
            <span className="health-score" style={{ color }}>{report.health_score}</span>
            <span className="health-score-label">/ 100</span>
          </div>
          <span className="health-title">Campaign Health Score</span>
        </div>
      </div>
      <div className="health-right">
        <span className={`health-risk-badge ${bgClass}`}>
          {report.risk_level} RISK
        </span>
        <p className="health-recommendation">{report.recommendation}</p>
      </div>
    </div>
  );
}

function BoolCard({ data, total }) {
  const yesPercent = Math.round(data.actual_rate * 100);
  const targetPercent = Math.round(data.target_rate * 100);
  const deviationOk = data.deviation < 0.1;

  return (
    <div className="bool-card" data-testid={`bool-card-${data.label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="bool-card-header">
        <span className="bool-card-label">{data.label}</span>
        <span className={`bool-dev ${deviationOk ? "ok" : "warn"}`}>
          {deviationOk ? <TrendingUp size={12} /> : <AlertTriangle size={12} />}
          {(data.deviation * 100).toFixed(1)}% dev
        </span>
      </div>
      <div className="bool-bars">
        <div className="bool-bar-row">
          <span className="bool-bar-label">Actual</span>
          <div className="bool-bar-track">
            <div
              className="bool-bar-fill actual"
              style={{ width: `${yesPercent}%` }}
            />
          </div>
          <span className="bool-bar-val">{yesPercent}%</span>
        </div>
        <div className="bool-bar-row">
          <span className="bool-bar-label">Target</span>
          <div className="bool-bar-track">
            <div
              className="bool-bar-fill target"
              style={{ width: `${targetPercent}%` }}
            />
          </div>
          <span className="bool-bar-val">{targetPercent}%</span>
        </div>
      </div>
      <div className="bool-counts">
        <span>Yes: {data.yes_count}</span>
        <span>No: {data.no_count}</span>
        <span>Total: {total}</span>
      </div>
    </div>
  );
}

function TextDistCard({ data, total }) {
  const fillRate = total > 0 ? Math.round((data.filled_count / total) * 100) : 0;

  return (
    <div className="text-dist-card" data-testid={`text-dist-${data.label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-dist-header">
        <span className="text-dist-label">{data.label}</span>
        <div className="text-dist-badges">
          <span className="text-dist-badge">{data.total_variants} variants</span>
          <span className="text-dist-badge">{data.unique_responses} unique</span>
          <span className="text-dist-badge">{fillRate}% filled</span>
        </div>
      </div>
      <div className="text-dist-bar-group">
        {data.variant_usage.map((v, i) => (
          <div key={i} className="text-dist-bar-row">
            <span className="text-dist-bar-idx">V{i + 1}</span>
            <div className="text-dist-bar-track">
              <div
                className="text-dist-bar-fill"
                style={{ width: `${total > 0 ? (v.count / total) * 100 : 0}%` }}
              />
            </div>
            <span className="text-dist-bar-count">{v.count}</span>
          </div>
        ))}
      </div>
      {data.omitted_count > 0 && (
        <span className="text-dist-omit">
          {data.omitted_count} sites omitted this field (natural variation)
        </span>
      )}
    </div>
  );
}
